<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import BarChart from 'shared/components/charts/BarChart.vue';
import ChatsWeekdayHeatmap from './ChatsWeekdayHeatmap.vue';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import {
  buildBucketArchiveFilters,
  resolveResolutionBreakdownMetricKey,
} from '../helpers/summaryReportArchiveHelper';
import {
  formatDynamicsBucketLabel,
  resolveDynamicsGranularity,
} from '../helpers/dynamicsGranularityHelper';
import { chartHasValues } from '../helpers/reportChartEmpty';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['chartsLoaded']);
const { t } = useI18n();
const loading = ref(false);
const chartData = ref({});
const { openArchive, isMetricLinkable } = useSummaryReportArchive();

const dynamicsGranularity = computed(
  () =>
    chartData.value.dynamics_group_by ||
    resolveDynamicsGranularity(props.filters.from, props.filters.to)
);

const incompleteText = computed(() => t('REPORT.COMPARISON.INCOMPLETE_DAY'));

const formatDynamicsLabels = rows =>
  rows.map(row =>
    formatDynamicsBucketLabel(
      row.date,
      dynamicsGranularity.value,
      incompleteText.value
    )
  );

let lastChartKey = '';

const fetchCharts = async () => {
  if (!props.filters.from) return;

  const chartKey = JSON.stringify({
    from: props.filters.from,
    to: props.filters.to,
    inboxIds: props.filters.inboxIds,
    teamIds: props.filters.teamIds,
    labelIds: props.filters.labelIds,
    excludeLabelIds: props.filters.excludeLabelIds,
    agentIds: props.filters.agentIds,
    resolutionMethod: props.filters.resolutionMethod,
    statusAtPeriodEnd: props.filters.statusAtPeriodEnd,
    reopening: props.filters.reopening,
    transfers: props.filters.transfers,
    humanResponse: props.filters.humanResponse,
  });
  if (chartKey === lastChartKey) return;
  lastChartKey = chartKey;

  loading.value = true;
  try {
    const { data } = await ReportsAPI.getSummaryCharts({
      type: 'chats',
      businessHours: props.filters.businessHours,
      inboxIds: props.filters.inboxIds,
      teamIds: props.filters.teamIds,
      labelIds: props.filters.labelIds,
      excludeLabelIds: props.filters.excludeLabelIds,
      agentIds: props.filters.agentIds,
      resolutionMethod: props.filters.resolutionMethod,
      statusAtPeriodEnd: props.filters.statusAtPeriodEnd,
      reopening: props.filters.reopening,
      transfers: props.filters.transfers,
      humanResponse: props.filters.humanResponse,
      from: props.filters.from,
      to: props.filters.to,
    });
    chartData.value = data || {};
    emit('chartsLoaded', chartData.value);
  } catch {
    chartData.value = {};
    emit('chartsLoaded', {});
    lastChartKey = '';
  } finally {
    loading.value = false;
  }
};

watch(() => props.filters, fetchCharts, { deep: true, immediate: true });

const rows = computed(() => chartData.value.daily_dynamics || []);

const cycleResultCollection = computed(() => ({
  labels: formatDynamicsLabels(rows.value),
  datasets: [
    {
      label: t('SUMMARY_REPORTS.CHARTS.SERIES.BOT_RESOLVED'),
      data: rows.value.map(row => row.bot_resolved || 0),
      backgroundColor: '#3B82F6',
      stack: 'cycle-result',
    },
    {
      label: t('SUMMARY_REPORTS.CHARTS.SERIES.HUMAN_RESOLVED'),
      data: rows.value.map(row => row.human_resolved || 0),
      backgroundColor: '#10B981',
      stack: 'cycle-result',
    },
    {
      label: t('SUMMARY_REPORTS.CHARTS.SERIES.UNRESOLVED'),
      data: rows.value.map(row => row.unresolved || 0),
      backgroundColor: '#F59E0B',
      stack: 'cycle-result',
    },
  ],
}));

const frtBucketsCollection = computed(() => {
  const bucketRows = chartData.value.frt_buckets || [];
  return {
    labels: bucketRows.map(row => {
      const key = `CHATS_REPORTS.BUCKETS.FRT.${row.key}`;
      const label = t(key);
      return label === key ? row.label || row.key : label;
    }),
    datasets: [
      {
        label: t('CHATS_REPORTS.CHARTS.FIRST_RESPONSE'),
        data: bucketRows.map(row => row.value || 0),
        backgroundColor: '#3B82F6',
      },
    ],
  };
});

const resolutionBucketsCollection = computed(() => {
  const bucketRows = chartData.value.resolution_buckets || [];
  return {
    labels: bucketRows.map(row => {
      const key = `CHATS_REPORTS.BUCKETS.RESOLUTION.${row.key}`;
      const label = t(key);
      return label === key ? row.label || row.key : label;
    }),
    datasets: [
      {
        label: t('CHATS_REPORTS.CHARTS.RESOLUTION_TIME'),
        data: bucketRows.map(row => row.value || 0),
        backgroundColor: '#10B981',
      },
    ],
  };
});

const heatmapHasValues = computed(() =>
  (chartData.value.weekday_heatmap || []).some(point => Number(point.value) > 0)
);

const stackedBarChartOptions = {
  scales: {
    x: { stacked: true, grid: { drawOnChartArea: false } },
    y: { stacked: true, beginAtZero: true, grid: { drawOnChartArea: false } },
  },
};

const openBucketPoint = (dateLabel, metricKey) => {
  if (!dateLabel || !isMetricLinkable(metricKey, 'account')) return;
  const filters = buildBucketArchiveFilters({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    dateLabel,
    granularity: dynamicsGranularity.value,
  });
  openArchive({
    metricKey,
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    filters,
  });
};

const onCycleResultClick = event => {
  const dateLabel = rows.value[event.index]?.date;
  openBucketPoint(
    dateLabel,
    resolveResolutionBreakdownMetricKey(event.datasetIndex)
  );
};

const onFrtBucketClick = event => {
  const bucket = (chartData.value.frt_buckets || [])[event.index];
  if (!bucket?.value) return;
  if (bucket.key === 'no_response') {
    openArchive({
      metricKey: 'unansweredEpisodesCount',
      reportType: 'account',
      row: null,
      context: props.archiveContext,
    });
    return;
  }
  openArchive({
    metricKey: 'firstResponseCount',
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    durationMin: bucket.min,
    durationMax: bucket.max,
    durationEvent: 'first_response',
  });
};

const onResolutionBucketClick = event => {
  const bucket = (chartData.value.resolution_buckets || [])[event.index];
  if (!bucket?.value) return;
  openArchive({
    metricKey: 'resolutionsCount',
    reportType: 'account',
    row: null,
    context: props.archiveContext,
    durationMin: bucket.min,
    durationMax: bucket.max,
    durationEvent: 'conversation_resolved',
  });
};
</script>

<template>
  <div v-if="loading" class="mt-4 text-sm text-n-slate-11">
    {{ $t('SUMMARY_REPORTS.CHARTS.LOADING') }}
  </div>
  <div v-else class="mt-4 flex flex-col gap-4">
    <p
      v-if="
        !heatmapHasValues &&
        !chartHasValues(frtBucketsCollection) &&
        !chartHasValues(resolutionBucketsCollection) &&
        !chartHasValues(cycleResultCollection)
      "
      class="px-2 py-6 text-center text-sm text-n-slate-11"
    >
      {{ $t('SUMMARY_REPORTS.CHARTS.NO_DATA') }}
    </p>
    <ChatsWeekdayHeatmap
      v-if="heatmapHasValues"
      :points="chartData.weekday_heatmap || []"
      :loading="loading"
      :archive-context="archiveContext"
    />

    <section
      v-if="chartHasValues(frtBucketsCollection)"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.FIRST_RESPONSE') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="frtBucketsCollection"
          :show-legend="false"
          @bar-click="onFrtBucketClick"
        />
      </div>
    </section>

    <section
      v-if="chartHasValues(resolutionBucketsCollection)"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.RESOLUTION_TIME') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="resolutionBucketsCollection"
          :show-legend="false"
          @bar-click="onResolutionBucketClick"
        />
      </div>
    </section>

    <section
      v-if="chartHasValues(cycleResultCollection)"
      class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CHATS_REPORTS.CHARTS.CYCLE_RESULT') }}
      </h3>
      <div class="mt-4 h-72">
        <BarChart
          :collection="cycleResultCollection"
          show-legend
          :chart-options="stackedBarChartOptions"
          @bar-click="onCycleResultClick"
        />
      </div>
    </section>
  </div>
</template>
